<div class="wpd-story" [class.is-desktop]="desktop()">

  <!-- Language switcher — lives OUTSIDE the scaled stage so it stays readable and
       tappable at every scale factor (the stage shrinks to ~0.55 on a small phone). -->
  @if (languages().length > 1) {
    <div class="wpd-story__lang">
      <button
        type="button"
        class="wpd-story__lang-toggle"
        [attr.aria-expanded]="langOpen()"
        aria-haspopup="listbox"
        (click)="toggleLangMenu($event)"
      >{{ currentLangLabel() }}<span class="caret">▼</span></button>

      @if (langOpen()) {
        <div class="wpd-story__lang-menu" role="listbox">
          @for (lang of languages(); track lang.code) {
            <button
              type="button"
              role="option"
              class="wpd-story__lang-item"
              [class.is-active]="lang.code === currentLang()"
              [attr.aria-selected]="lang.code === currentLang()"
              (click)="selectLanguage(lang.code)"
            >
              <span class="name">{{ lang.name }}</span>
              <span class="code">{{ lang.label }}</span>
            </button>
          }
        </div>
      }
    </div>
  }

  <div class="stage-box" #stageBox>
    <div class="deck-wrap" #deckWrap>
      <img class="bridge" #bridge [src]="asset('background.jpg')" alt="" />
      <div class="deck">

        <!-- ═══════════ SCENE 1 ═══════════ -->
        <section class="scene s1" data-frames="scene01_frame01,scene01_frame02">
          <div class="pin">
            <div class="stage"></div>
            <div class="vig"></div>
            <div class="brand st" data-in="logo"><img [src]="asset('wplogo.svg')" alt="WolfPack" /></div>
            <div class="s1copy">
              <h1 class="hl big">
                <span class="wht st" data-in="h1">
                  @for (line of lines('funnel.story.s1.h1' | translate); track $index) {
                    <span class="ln">{{ line }}</span>
                  }
                </span>
                <span class="grn g2 st" data-in="h2">
                  @for (line of lines('funnel.story.s1.h2' | translate); track $index) {
                    <span class="ln">{{ line }}</span>
                  }
                </span>
              </h1>
              <img class="line st" data-in="sub" [src]="asset('line.png')" alt="" />
              <p class="subline st" data-in="sub">
                @for (part of lines('funnel.story.s1.subline' | translate); track $index) {
                  @if (!$first) {<span class="dot">•</span>}{{ part }}
                }
              </p>
            </div>
            <div class="cue st" data-in="logo">
              <span>{{ 'funnel.story.s1.cue' | translate }}</span><i class="chev"></i>
              <img class="cueglow" [src]="asset('bottom.png')" alt="" />
            </div>
          </div>
        </section>

        <!-- ═══════════ SCENE 2 ═══════════ -->
        <section class="scene s2" data-frames="scene02_frame01" data-step="420">
          <div class="stage"></div><div class="vig"></div>
          <div class="brand r"><img [src]="asset('wplogo.svg')" alt="WolfPack" /></div>
          <div class="s2h">
            <h1 class="hl s2big">
              @for (word of lines('funnel.story.s2.words' | translate); track $index) {
                <span class="w" [class.wht]="!$last" [class.grn]="$last" [style.animation-delay.s]="$index * 0.22">{{ word }}</span>
              }
            </h1>
          </div>
          <div class="s2sub">
            <p class="subtxt">{{ 'funnel.story.s2.sub' | translate }}</p>
            <img class="line s2line" [src]="asset('line.png')" alt="" />
          </div>
          <div class="cue">
            <i class="chev"></i>
            <img class="cueglow" [src]="asset('bottom.png')" alt="" />
          </div>
        </section>

        <!-- ═══════════ SCENE 3 (health) ═══════════ -->
        <section class="scene s3" data-bg="black" data-frames="scene03_frame01" data-step="420">
          <div class="stage"></div><div class="vig"></div>
          <div class="brand r"><img [src]="asset('wplogo.svg')" alt="WolfPack" /></div>
          <div class="s3copy">
            <p class="ey s3ey">{{ 'funnel.story.s3.eyebrow' | translate }}</p>
            <h1 class="hl s3big grn">{{ 'funnel.story.s3.headline' | translate }}</h1>
            <p class="s3sub r d3">{{ 'funnel.story.s3.sub' | translate }}</p>
            <img class="line r d4" [src]="asset('line.png')" alt="" />
            <p class="r d5">
              <span class="pill pill-lg">
                <b>{{ 'funnel.story.s3.pill_label' | translate }}</b> • {{ 'funnel.story.s3.pill_price' | translate }}
              </span>
            </p>
          </div>
          <div class="cue"><i class="chev"></i><img class="cueglow" [src]="asset('bottom.png')" alt="" /></div>
        </section>

        <!-- ═══════════ SCENE 4 (duplicate / depth) ═══════════ -->
        <section class="scene s4"
                 data-frames="scene04_frame01,scene04_frame02,scene04_frame03,scene04_frame04,scene04_frame05"
                 data-step="400">
          <div class="stage"></div><div class="vig"></div>
          <div class="brand r"><img [src]="asset('wplogo.svg')" alt="WolfPack" /></div>
          <p class="s4dep">{{ 'funnel.story.s4.depth' | translate }}</p>
          <div class="s4copy">
            <h1 class="hl s4big">
              <span class="wht dpA">{{ 'funnel.story.s4.a' | translate }}</span>
              <span class="grn r dpB">{{ 'funnel.story.s4.b' | translate }}</span>
            </h1>
          </div>
          <div class="cue"><i class="chev"></i><img class="cueglow" [src]="asset('bottom.png')" alt="" /></div>
        </section>

        <!-- ═══════════ SCENE 5 ═══════════ -->
        <section class="scene s5"
                 data-frames="scene05_frame01,scene05_frame02,scene05_frame03,scene05_frame04,scene05_frame05,scene05_frame06"
                 data-step="550">
          <div class="stage"></div><div class="vig"></div>
          <div class="brand r"><img [src]="asset('wplogo.svg')" alt="WolfPack" /></div>
          <div class="s5cap">
            <p class="s5captxt">{{ 'funnel.story.s5.caption' | translate }}</p>
            <img class="line" [src]="asset('line.png')" alt="" />
          </div>
          <div class="cue"><i class="chev"></i><img class="cueglow" [src]="asset('bottom.png')" alt="" /></div>
        </section>

        <!-- ═══════════ SCENE 6 (platinum) ═══════════ -->
        <section class="scene s6" data-frames="scene06_frame01" data-step="450">
          <div class="stage"></div><div class="vig"></div>
          <div class="brand r"><img [src]="asset('wplogo.svg')" alt="WolfPack" /></div>
          <div class="s6copy">
            <p class="ey s6ey">{{ 'funnel.story.s6.eyebrow' | translate }}</p>
            <h1 class="hl s6no grn">{{ 'funnel.story.s6.answer' | translate }}</h1>
            <h2 class="hl s6plat wht">
              @for (part of lines('funnel.story.s6.lines' | translate); track $index) {
                <span class="r" [class.p1]="$first" [class.p2]="!$first">{{ part }}</span>
              }
            </h2>
          </div>
          <div class="cue"><i class="chev"></i><img class="cueglow" [src]="asset('bottom.png')" alt="" /></div>
        </section>

        <!-- ═══════════ SCENE 7 (platinum lines / multipliers) ═══════════ -->
        <section class="scene s7"
                 data-frames="scene07_frame01,scene07_frame02,scene07_frame03,scene07_frame04">
          <div class="stage"></div><div class="vig"></div>
          <div class="brand r"><img [src]="asset('wplogo.svg')" alt="WolfPack" /></div>
          <div class="s7top">
            <p class="l0 pl">{{ 'funnel.story.s7.line_label' | translate }}</p>
            <p class="l pl">{{ 'funnel.story.s7.amount' | translate }}</p>
            <p class="l pl">{{ 'funnel.story.s7.amount' | translate }}</p>
            <p class="l pl money">
              <span class="amt">{{ 'funnel.story.s7.amount' | translate }}<small>{{ 'funnel.story.s7.per_month' | translate }}</small></span>
            </p>
          </div>
          <h2 class="hl s7fm">
            @for (part of lines('funnel.story.s7.fm' | translate); track $index) {
              <span class="ln" [class.wht]="$first" [class.grn]="!$first">{{ part }}</span>
            }
          </h2>
          <div class="cue"><i class="chev"></i><img class="cueglow" [src]="asset('bottom.png')" alt="" /></div>
        </section>

        <!-- ═══════════ SCENE 9 (final / CTA) ═══════════ -->
        <section class="scene s9" data-frames="scene09_frame01" data-step="420">
          <div class="stage"></div><div class="vig"></div>
          <div class="brand r"><img [src]="asset('wplogo.svg')" alt="WolfPack" /></div>
          <div class="inner center">
            <div class="flex1"></div>
            <div class="stack" style="padding-bottom: 40px">
              <h1 class="hl big r d1">
                @for (part of lines('funnel.story.s9.headline' | translate); track $index) {
                  <span class="ln" [class.wht]="$first" [class.grn]="!$first">{{ part }}</span>
                }
              </h1>
              <p class="getstarted r d2">{{ 'funnel.story.s9.get_started' | translate }}</p>
              <!-- &nbsp; and not a plain space: Angular strips inter-element whitespace (preserveWhitespaces defaults to false), which glued the two words together. -->
              <h2 class="hl mid r d2"><span class="wht">WolfPack</span>&nbsp;<span class="grn">Distribution</span></h2>
              <p class="r d3" style="margin-top: 26px">
                <a class="cta" role="button" tabindex="0" (click)="goToApplication()" (keydown.enter)="goToApplication()">
                  <img class="btn-base" [src]="asset('button_blank.png')" [alt]="'funnel.story.s9.cta' | translate" />
                  <img class="btn-hi" [src]="asset('button_highlight_blank.png')" alt="" />
                  <span class="btn-label"><span #ctaLabel>{{ 'funnel.story.s9.cta' | translate }}</span></span>
                </a>
              </p>
            </div>
          </div>
        </section>

      </div>
      <div class="edgevig"></div>
    </div>
  </div>
</div>
